/**
 * 表格导入弹窗（选择导入为在线表格 OR 数据表）
 */
import {useCallback} from 'react';
import cx from 'classnames';

import * as store from '@/store';
import {
    feLogger,
    importExcelFromWeb,
    importExcelDstFromWeb,
} from '@/buried';
import Modal from '@/components/Modal';
import Button, {ButtonShape, ButtonType, ButtonSize} from '@/components/Button';
import Icon, {IconSize} from '@/components/Icon';
import {DialogCloseSvg} from '@/icons';
import type {UploadInputOptions, ExcelImportDialogData} from '@/types/document/file';
import {FileType} from '@/constants/document/file';
import SheetThumbnail from '@/assets/img/sheet-thumbnail.png';
import DataSheetThumbnail from '@/assets/img/datasheet-thumbnail.png';

import styles from './index.module.scss';
import kuPiper from '@/middleware/kuPiper';

interface CardItem {
    imgSrc: string;
    type: string;
    desc: string;
    key: string;
    onUpload: () => void;
}

export interface IProps {
    importExcelDialogData?: ExcelImportDialogData;
}

const ImportExcelDialog: React.FC<IProps> = ({importExcelDialogData}) => {
    const {
        visible,
        curDocument,
        source,
        repositoryGuid,
        userGuid,
    } = importExcelDialogData as ExcelImportDialogData;

    const handleCancel = useCallback(() => {
        store.setImportExcelDialogData({visible: false});
    }, []);

    const getCardItem = (data: CardItem) => {
        const {imgSrc, type, desc, onUpload, key} = data;

        return (
            <div className={styles.card} key={key}>
                <img src={imgSrc} alt="" />
                <p className={styles.type}>{type}</p>
                <p className={styles.desc}>{desc}</p>
                <Button
                    className={cx(styles.button, styles.confirmButton)}
                    onClick={onUpload}
                    size={ButtonSize.MD}
                    type={ButtonType.Important}
                    shape={ButtonShape.Capsule}
                >
                    立即导入
                </Button>
            </div>
        );
    };

    const renderBody = () => {
        // 上报埋点参数
        const buriedParams = {
            kb_id: repositoryGuid,
            userid: userGuid,
            source,
        };

        const CardList: CardItem[] = kuPiper.openListSlot(
            {
                scope: kuPiper.slotScopeMap.SpaceRepo,
                position: kuPiper.slotPositionMap.SpaceRepo.ImportLocalFile,
                originList: [
                    {
                        imgSrc: SheetThumbnail,
                        type: '在线表格',
                        desc: '知识库在线表格，简单易用',
                        key: 'inlineSheet',
                        onUpload: () => {
                            // 导入逻辑统一放置 UploadInput 组件
                            store.setUploadInputOptions({
                                fileType: FileType.Excel,
                                curDocument,
                                source,
                            } as UploadInputOptions);
                            handleCancel();

                            feLogger(importExcelFromWeb(buriedParams));
                        },
                    },
                    {
                        imgSrc: DataSheetThumbnail,
                        type: '数据表',
                        desc: '多视图自由切换，更专业的表格系统',
                        key: 'dataSheet',
                        onUpload: () => {
                            // 导入逻辑统一放置 UploadInput 组件
                            store.setUploadInputOptions({
                                fileType: FileType.ExcelDst,
                                curDocument,
                                source,
                            } as UploadInputOptions);
                            handleCancel();

                            feLogger(importExcelDstFromWeb(buriedParams));
                        },
                    },
                ],
            });

        return CardList.map(item => getCardItem(item));
    };

    return (
        <Modal
            className={styles.modal}
            visible={visible}
            destroyOnHide
        >
            <div className={styles.wrapper}>
                <div className={styles.header}>
                    <span className={styles.title}>选择将表格导入为</span>
                    <Icon
                        noSpace
                        className={styles.icon}
                        svg={DialogCloseSvg}
                        size={IconSize.XXL}
                        onClick={handleCancel}
                    />
                </div>
                <div className={styles.divide} />
                <div className={styles.body}>{renderBody()}</div>
            </div>
        </Modal>
    );
};


export default store.connect([
    store.KEYS.IMPORT_EXCEL_DIALOG_DATA,
])(ImportExcelDialog);
